import React from 'react';
import { Bell, Clock, Hand, MessageSquare, Webhook } from 'lucide-react';
import { TemplateIcon } from 'features/agents/templates/templateIcons';

/** What starts an agent, in the words the list page uses. */
export const TRIGGERS = {
  alert: { label: 'Alert', Icon: Bell },
  schedule: { label: 'Schedule', Icon: Clock },
  webhook: { label: 'Webhook', Icon: Webhook },
  manual: { label: 'Manual', Icon: Hand },
  slack_event: { label: 'Slack', Icon: MessageSquare },
};

/** Used when the catalog response carries no `needs` label map. */
export const DEFAULT_NEEDS = {
  alerts: 'Alerts',
  slack: 'Slack',
  github: 'GitHub',
  coding_agent: 'A coding agent',
  model: 'A model',
  decision_model: 'A decision model',
  linear: 'Linear',
  webhook: 'A webhook',
  python: 'Python',
  http_endpoint: 'An HTTP endpoint',
};

export const MAX_NEEDS = 3;

/** One template: its icon, name, two lines of what it does, what starts it and what it needs. */
export default function TemplateCard({ template: t, needsLabels, onPick, index = 0 }) {
  const trig = TRIGGERS[t.trigger] || { label: t.trigger || 'Manual', Icon: Hand };
  const labels = needsLabels || DEFAULT_NEEDS;
  const needs = (t.needs || []).filter((n) => n !== t.trigger);
  const shown = needs.slice(0, MAX_NEEDS);
  const extra = needs.length - shown.length;
  const TrigIcon = trig.Icon;
  return (
    <button
      type="button"
      data-template-card
      data-testid={`template-${t.id}`}
      onClick={() => onPick(t)}
      style={{ animationDelay: `${Math.min(index, 8) * 25}ms` }}
      className="agent-template-card group flex min-h-[112px] flex-col items-start gap-2 rounded-xl border border-[color:var(--border-default)] bg-surface p-3 text-left transition-colors hover:border-[color:var(--border-strong)] hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]"
    >
      <span className="flex w-full items-center gap-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-base)] text-[color:var(--text-secondary)]">
          <TemplateIcon name={t.icon} size={16} />
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[color:var(--text-primary)]">{t.name}</span>
      </span>
      <span className="line-clamp-2 text-[12px] leading-snug text-[color:var(--text-muted)]">{t.description}</span>
      <span className="mt-auto flex flex-wrap items-center gap-1">
        <span className="inline-flex items-center gap-1 rounded-md border border-[color:var(--border-default)] px-1.5 py-0.5 text-[10.5px] font-medium text-[color:var(--text-secondary)]">
          <TrigIcon size={10} aria-hidden /> {trig.label}
        </span>
        {shown.map((n) => (
          <span key={n} className="rounded-md bg-[color:var(--surface-sunken)] px-1.5 py-0.5 text-[10.5px] text-[color:var(--text-muted)]">{labels[n] || n}</span>
        ))}
        {extra > 0 && <span className="text-[10.5px] text-[color:var(--text-muted)]">+{extra}</span>}
      </span>
    </button>
  );
}
